import React, { useState, useEffect, useCallback } from 'react';
import { 
  Key, Plus, Copy, Eye, EyeOff, Trash2, RefreshCw, 
  Shield, Smartphone, Monitor, Tv, Globe, Settings,
  CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';

// Platform configurations
const PLATFORMS = {
  apple: {
    id: 'apple',
    name: 'Apple (iOS + tvOS)',
    icon: Smartphone,
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/10',
    description: 'iPhone, iPad, Apple TV'
  },
  android: {
    id: 'android',
    name: 'Android',
    icon: Smartphone,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    description: 'Phones, Tablets, Android TV, Fire TV'
  },
  roku: {
    id: 'roku',
    name: 'Roku',
    icon: Tv,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    description: 'Roku devices and TVs'
  },
  web: {
    id: 'web',
    name: 'Web',
    icon: Globe,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    description: 'Web browsers'
  },
  admin: {
    id: 'admin',
    name: 'Admin Panel',
    icon: Settings,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    description: 'AdminUI / Internal tools'
  }
};

// Generate a random API key
const generateApiKey = (platform) => {
  const chars = 'abcdef0123456789';
  let key = '';
  for (let i = 0; i < 32; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${platform}_${key}`;
};

export function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [newKey, setNewKey] = useState({
    platform: '',
    notes: '',
    key: ''
  });

  // Load keys from localStorage (will connect to backend later)
  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      // Try to load from backend first
      try {
        const data = await api.getApiKeys();
        if (data && data.keys) {
          setKeys(data.keys);
          setIsLoading(false);
          return;
        }
      } catch (err) {
        // Backend endpoint doesn't exist yet, use localStorage
        console.log('Backend API keys endpoint not available, using localStorage');
      }
      
      // Fallback to localStorage
      const stored = localStorage.getItem('railstream_api_keys');
      if (stored) {
        setKeys(JSON.parse(stored));
      } else {
        // Initialize with the current keys
        const initialKeys = [
          {
            id: '1',
            platform: 'apple',
            key: 'apple_52a9f40a7626c8ddf8a9bcccaf2ba47c',
            created_at: new Date().toISOString(),
            notes: 'Production key for iOS and tvOS apps',
            status: 'active'
          },
          {
            id: '2',
            platform: 'android',
            key: 'android_712835409dce703df1e7bb1594922dd8',
            created_at: new Date().toISOString(),
            notes: 'Production key for Android phones, tablets, Android TV, Fire TV',
            status: 'active'
          },
          {
            id: '3',
            platform: 'roku',
            key: 'roku_db5b1de648be190c22426a3d253eaa4e',
            created_at: new Date().toISOString(),
            notes: 'Production key for Roku devices',
            status: 'active'
          },
          {
            id: '4',
            platform: 'web',
            key: 'web_9c8f46fd56486a168cca8cb9363b2f47',
            created_at: new Date().toISOString(),
            notes: 'Production key for web browser app',
            status: 'active'
          },
          {
            id: '5',
            platform: 'admin',
            key: 'admin_47ddfda717b74045571bde44323e9e21',
            created_at: new Date().toISOString(),
            notes: 'Admin panel / internal tools',
            status: 'active'
          }
        ];
        setKeys(initialKeys);
        localStorage.setItem('railstream_api_keys', JSON.stringify(initialKeys));
      }
    } catch (err) {
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  // Save keys to localStorage
  const saveKeys = (updatedKeys) => {
    setKeys(updatedKeys);
    localStorage.setItem('railstream_api_keys', JSON.stringify(updatedKeys));
  };

  // Toggle key visibility
  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  // Copy key to clipboard
  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };

  // Handle add new key
  const handleAddKey = () => {
    if (!newKey.platform) {
      toast.error('Please select a platform');
      return;
    }

    const key = newKey.key || generateApiKey(newKey.platform);
    const newKeyObj = {
      id: Date.now().toString(),
      platform: newKey.platform,
      key: key,
      created_at: new Date().toISOString(),
      notes: newKey.notes,
      status: 'active'
    };

    const updatedKeys = [...keys, newKeyObj];
    saveKeys(updatedKeys);
    
    setShowAddDialog(false);
    setNewKey({ platform: '', notes: '', key: '' });
    toast.success('API key created');
  };

  // Handle delete key
  const handleDeleteKey = () => {
    if (!keyToDelete) return;
    
    const updatedKeys = keys.filter(k => k.id !== keyToDelete.id);
    saveKeys(updatedKeys);
    
    setShowDeleteDialog(false);
    setKeyToDelete(null);
    toast.success('API key revoked');
  };

  // Mask key for display
  const maskKey = (key) => {
    if (!key) return '';
    const prefix = key.split('_')[0];
    return `${prefix}_${'•'.repeat(24)}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Key className="h-8 w-8 text-primary" />
            API Keys
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage API keys for RailStream apps and services
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadKeys} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-key-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Key
          </Button>
        </div>
      </div>

      {/* Security Notice */}
      <Card className="border-yellow-500/50 bg-yellow-500/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-500">Security Notice</p>
              <p className="text-sm text-muted-foreground">
                API keys authenticate your apps with the RailStream API. Keep them secret. 
                If a key is compromised, revoke it immediately and generate a new one.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {Object.values(PLATFORMS).map(platform => {
          const platformKey = keys.find(k => k.platform === platform.id && k.status === 'active');
          const Icon = platform.icon;
          
          return (
            <Card key={platform.id} className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${platform.bgColor}`}>
                    <Icon className={`h-5 w-5 ${platform.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{platform.name}</p>
                    <p className="text-xs text-muted-foreground">{platform.description}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  {platformKey ? (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      <XCircle className="mr-1 h-3 w-3" />
                      No Key
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Keys Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Active API Keys
          </CardTitle>
          <CardDescription>
            All API keys currently in use across platforms
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading keys...
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No API keys configured</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowAddDialog(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add First Key
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Platform</TableHead>
                  <TableHead>API Key</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((keyItem) => {
                  const platform = PLATFORMS[keyItem.platform] || PLATFORMS.web;
                  const Icon = platform.icon;
                  const isVisible = visibleKeys[keyItem.id];
                  
                  return (
                    <TableRow key={keyItem.id} data-testid={`key-row-${keyItem.platform}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${platform.bgColor}`}>
                            <Icon className={`h-4 w-4 ${platform.color}`} />
                          </div>
                          <span className="font-medium">{platform.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-secondary px-2 py-1 rounded font-mono">
                          {isVisible ? keyItem.key : maskKey(keyItem.key)}
                        </code>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(keyItem.created_at)}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <span className="text-sm text-muted-foreground truncate block">
                          {keyItem.notes || '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {keyItem.status === 'active' ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="destructive">Revoked</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleKeyVisibility(keyItem.id)}
                            data-testid={`toggle-visibility-${keyItem.platform}`}
                          >
                            {isVisible ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyKey(keyItem.key)}
                            data-testid={`copy-key-${keyItem.platform}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setKeyToDelete(keyItem);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-key-${keyItem.platform}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Key Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for a platform. You can auto-generate or enter a specific key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select
                value={newKey.platform}
                onValueChange={(value) => setNewKey(prev => ({ ...prev, platform: value }))}
              >
                <SelectTrigger data-testid="platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(PLATFORMS).map(platform => (
                    <SelectItem key={platform.id} value={platform.id}>
                      <div className="flex items-center gap-2">
                        <platform.icon className={`h-4 w-4 ${platform.color}`} />
                        {platform.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>API Key (optional - leave blank to auto-generate)</Label>
              <Input
                placeholder={newKey.platform ? `${newKey.platform}_xxxxxxxxxxxxxxxx` : 'Select platform first'}
                value={newKey.key}
                onChange={(e) => setNewKey(prev => ({ ...prev, key: e.target.value }))}
                className="font-mono"
                data-testid="key-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Description or notes about this key..."
                value={newKey.notes}
                onChange={(e) => setNewKey(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddKey} data-testid="save-key-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key?</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately invalidate the key for{' '}
              <strong>{keyToDelete && PLATFORMS[keyToDelete.platform]?.name}</strong>.
              Any apps using this key will stop working until updated with a new key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteKey}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ApiKeys;
